/**
 * Map Coverage sandbox / BFF payloads into the customer UI view model.
 * The SPA never reads PayerMesh coverage_summary or the old scheduled-patients catalog.
 *
 * Nested enrichment from Coverage API is forwarded as-is by the BFF. This mapper
 * reads current sandbox keys plus the intended enriched shape:
 *   benefits_summary, limits, ortho / orthodontics, dental_policy, cob,
 *   payer_knowledge, coverage_intelligence.citations / evidence
 * Missing sections become explicit empty states — values are never invented.
 */

export function splitPatientName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

export function mapVisitReadiness(readinessStatus) {
  const status = String(readinessStatus || "").toUpperCase();
  if (status === "READY" || status === "COVERAGE_AVAILABLE") return "ready";
  if (status === "BLOCKED" || status === "FAILED") return "blocked";
  if (status === "NEEDS_REVIEW" || status === "NOT_CHECKED" || status === "PARTIAL") {
    return "attention_required";
  }
  return "attention_required";
}

export function formatMoneyField(field) {
  if (!field || field.status === "NOT_PROVIDED_BY_PAYER" || field.value === null || field.value === undefined) {
    return "Not provided";
  }
  const unit = field.unit;
  const value = field.value;
  if (unit === "USD" || unit === "usd") {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (unit === "RATIO") {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return `${Math.round(n * 100)}%`;
  }
  return String(value);
}

export function formatPercent(field) {
  if (!field || field.value === null || field.value === undefined) return "—";
  const n = Number(field.value);
  if (Number.isNaN(n)) return "—";
  if (n <= 1) return `${Math.round(n * 100)}%`;
  return `${Math.round(n)}%`;
}

function isBlank(value) {
  return value === null || value === undefined || value === "";
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (isBlank(value)) return [];
  return [value];
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasContent(value) {
  if (isBlank(value)) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (isPlainObject(value)) return Object.keys(value).length > 0;
  return true;
}

function normalizeKey(key) {
  return String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function pickKey(obj, names) {
  if (!isPlainObject(obj)) return undefined;
  for (const name of names) {
    if (Object.prototype.hasOwnProperty.call(obj, name) && obj[name] !== undefined) {
      return obj[name];
    }
  }
  const lookup = {};
  for (const [key, value] of Object.entries(obj)) {
    lookup[normalizeKey(key)] = value;
  }
  for (const name of names) {
    const hit = lookup[normalizeKey(name)];
    if (hit !== undefined) return hit;
  }
  return undefined;
}

export function firstRecord(...candidates) {
  for (const candidate of candidates) {
    if (hasContent(candidate)) return candidate;
  }
  return null;
}

export function formatField(field) {
  if (isBlank(field)) return null;
  if (typeof field === "boolean") return field ? "Yes" : "No";
  if (typeof field === "number") return String(field);
  if (typeof field === "string") {
    const trimmed = field.trim();
    return trimmed || null;
  }
  if (Array.isArray(field)) {
    const parts = field.map(formatField).filter(Boolean);
    return parts.length ? parts.join("; ") : null;
  }
  if (isPlainObject(field)) {
    const status = field.status;
    const value = field.value;
    if (status === "NOT_PROVIDED_BY_PAYER" && isBlank(value)) return null;
    if (!isBlank(value)) {
      if (typeof value === "boolean") return value ? "Yes" : "No";
      const unit = field.unit;
      if (unit === "USD" || unit === "usd") return formatMoneyField(field);
      if (unit === "RATIO") return formatPercent(field);
      if (unit) return `${value} ${unit}`.trim();
      return String(value);
    }
    return (
      formatField(field.text) ||
      formatField(field.summary) ||
      formatField(field.description) ||
      formatField(field.message) ||
      formatField(field.label) ||
      formatField(field.notes) ||
      null
    );
  }
  return String(field);
}

export function displayOrMissing(field) {
  return formatField(field) || "Not provided";
}

function joinNetworkPercents(current, next) {
  if (!next || next === "—") return current || "—";
  if (!current || current === "—") return next;
  if (current === next || String(current).split(" / ").includes(next)) return current;
  return `${current} / ${next}`;
}

function uniqueBenefits(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    const types = (row.service_types || []).join("|");
    const codes = (row.service_type_codes || []).join("|");
    const key = `${types}|${codes}|${row.network}|${row.plan_coverage}|${row.percent?.value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function buildBenefitMatrix(serviceBenefits, financialBenefits) {
  const grouped = {};
  const ensure = (label, codes) => {
    if (!grouped[label]) {
      grouped[label] = {
        label,
        codes: codes || [],
        in_network: "—",
        out_of_network: "—",
        member_in: "—",
        member_out: "—",
        is_ortho: isOrthoLabel(label),
      };
    } else if (codes?.length) {
      const merged = new Set([...(grouped[label].codes || []), ...codes]);
      grouped[label].codes = Array.from(merged);
    }
    return grouped[label];
  };

  for (const row of uniqueBenefits(serviceBenefits)) {
    const label = (row.service_types && row.service_types[0]) || "Other";
    const entry = ensure(label, row.service_type_codes || []);
    const pct = formatPercent(row.percent);
    const network = String(row.network || "").toUpperCase();
    if (network.includes("IN_NETWORK") || network === "IN") {
      entry.in_network = joinNetworkPercents(entry.in_network, pct);
    } else if (network.includes("OUT")) {
      entry.out_of_network = joinNetworkPercents(entry.out_of_network, pct);
    }
  }

  for (const row of financialBenefits || []) {
    if (String(row.benefit_type || "").toLowerCase() !== "coinsurance") continue;
    const label = (row.service_types && row.service_types[0]) || "Other";
    const entry = ensure(label, row.service_type_codes || []);
    const pct = formatPercent(row.percent);
    const network = String(row.network || "").toUpperCase();
    if (network.includes("IN_NETWORK") || network === "IN") {
      entry.member_in = joinNetworkPercents(entry.member_in, pct);
    } else if (network.includes("OUT")) {
      entry.member_out = joinNetworkPercents(entry.member_out, pct);
    }
  }

  return Object.values(grouped);
}

function isOrthoLabel(value) {
  return /ortho/i.test(String(value || ""));
}

function isWaitingItem(item) {
  const types = asArray(item?.service_types).join(" ").toLowerCase();
  const text = [
    item?.text,
    item?.limitation_type,
    ...(asArray(item?.additional_information)),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return types.includes("waiting") || text.includes("waiting period") || text.includes("wait start");
}

function citationUrl(cit) {
  if (!isPlainObject(cit)) return null;
  return (
    formatField(pickKey(cit, ["url", "source_url", "document_url", "link", "href", "evidence_url"])) ||
    null
  );
}

export function normalizeCitation(cit) {
  if (!isPlainObject(cit)) return null;
  const claimText = formatField(
    pickKey(cit, ["claim_text", "text", "summary", "quote", "source_text"])
  );
  const documentName = formatField(
    pickKey(cit, ["document_name", "document", "title", "source_name"])
  );
  if (!claimText && !documentName && !cit.citation_id) return null;
  return {
    citation_id: cit.citation_id || cit.id || cit.claim_id || documentName || claimText,
    claim_text: claimText,
    document_name: documentName,
    document_version: formatField(cit.document_version),
    page_number: cit.page_number ?? cit.page ?? cit.page_start ?? null,
    section: formatField(cit.section),
    source_text: formatField(cit.source_text),
    grounding_status: formatField(cit.grounding_status),
    payer: formatField(cit.payer),
    policy_number: formatField(cit.policy_number),
    effective_date: formatField(cit.effective_date),
    url: citationUrl(cit),
    raw: cit,
  };
}

export function collectCitations(...groups) {
  const out = [];
  const seen = new Set();
  for (const group of groups) {
    for (const item of asArray(group)) {
      const cit = normalizeCitation(item);
      if (!cit) continue;
      const key = cit.citation_id || `${cit.document_name}|${cit.claim_text}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(cit);
    }
  }
  return out;
}

function firstArray(...candidates) {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) return candidate;
  }
  return [];
}

function unwrapPayload(bffResponse) {
  return bffResponse?.payload || bffResponse || {};
}

export function mapReadinessItems(bffResponse) {
  const payload = unwrapPayload(bffResponse);
  const items = Array.isArray(payload.items) ? payload.items : [];
  return items.map((item) => {
    const names = splitPatientName(item.patient_name);
    const visitReadiness = mapVisitReadiness(item.readiness_status);
    const orthoFlag = firstRecord(
      item.ortho_status,
      item.orthodontics_status,
      item.has_ortho,
      item.has_orthodontics
    );
    return {
      patient_id: String(item.patient_id),
      first_name: names.first_name,
      last_name: names.last_name,
      patient_name: item.patient_name || `${names.first_name} ${names.last_name}`.trim(),
      appointment_id: item.appointment_id || null,
      appointment_datetime: item.appointment_datetime || null,
      appointment_status: item.appointment_status || null,
      readiness_status: item.readiness_status || null,
      readiness_score: item.readiness_score,
      reasons: item.reasons || [],
      recommended_action: item.recommended_action || null,
      review_id: item.review_id || null,
      eligibility_status: item.eligibility_status || "NOT_CHECKED",
      coverage_intelligence_status: item.coverage_intelligence_status || null,
      payer_knowledge_status: item.payer_knowledge_status || null,
      visit_readiness: visitReadiness,
      next_best_action: item.recommended_action || "Verify coverage",
      snapshot_ref: item.snapshot_ref || null,
      workflow_id: item.workflow_id || null,
      ortho_status: formatField(orthoFlag),
      has_ortho: Boolean(orthoFlag) && String(orthoFlag).toLowerCase() !== "false",
    };
  });
}

function extractWaitingPeriods(payload, ci, eligibility) {
  const explicit = firstRecord(
    payload.benefits_summary?.waiting_periods,
    payload.limits?.waiting_periods,
    payload.waiting_periods,
    ci.waiting_periods
  );
  const sourceRows = hasContent(explicit)
    ? asArray(explicit)
    : [...asArray(ci.limitations), ...asArray(eligibility.limitations)].filter(isWaitingItem);

  return sourceRows
    .map((row) => {
      const text =
        formatField(pickKey(row, ["text", "summary", "description", "message"])) ||
        formatField(row.additional_information) ||
        (asArray(row.service_types).join(", ") || "Waiting period");
      const quantity = formatField(row.quantity);
      if (!text && !quantity) return null;
      return {
        text,
        quantity,
        service_types: asArray(row.service_types),
        status: formatField(row.status),
      };
    })
    .filter(Boolean);
}

function extractFrequencyLimits(payload, ci, payerKnowledge, codeRules) {
  const explicit = firstRecord(
    payload.benefits_summary?.frequency_remaining,
    payload.limits?.frequency,
    payload.limits?.frequency_remaining,
    payload.frequency_remaining,
    ci.frequency_remaining
  );
  if (hasContent(explicit)) {
    return asArray(explicit)
      .map((row) => {
        if (!isPlainObject(row)) {
          const text = formatField(row);
          return text ? { label: "Frequency", value: text } : null;
        }
        return {
          label:
            formatField(pickKey(row, ["label", "service", "code", "category", "name"])) ||
            "Frequency",
          value: displayOrMissing(
            pickKey(row, ["remaining", "value", "limit", "frequency_limit", "text", "summary"])
          ),
          remaining: formatField(pickKey(row, ["remaining", "remaining_value"])),
          status: formatField(row.status),
        };
      })
      .filter(Boolean);
  }

  const fromRules = asArray(codeRules)
    .map((rule) => {
      const limit = formatField(rule.frequency_limit);
      if (!limit) return null;
      return {
        label: [rule.code, rule.description].filter(Boolean).join(" · ") || "Procedure",
        value: limit,
        remaining: null,
        status: formatField(rule.frequency_limit?.status),
      };
    })
    .filter(Boolean);

  const fromAssessments = asArray(ci.service_assessments)
    .map((svc) => {
      const limit = formatField(svc.frequency_limit);
      if (!limit) return null;
      return {
        label: [svc.code, svc.service].filter(Boolean).join(" · ") || "Service",
        value: limit,
        remaining: formatField(svc.frequency_remaining),
        status: formatField(svc.frequency_limit?.status),
      };
    })
    .filter(Boolean);

  const merged = [];
  const seen = new Set();
  for (const row of [...fromAssessments, ...fromRules]) {
    const key = `${row.label}|${row.value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }
  return merged;
}

function extractCob(payload, eligibility, memberCoverage, ci) {
  const cob = firstRecord(
    payload.benefits_summary?.cob,
    payload.benefits_summary?.coordination_of_benefits,
    payload.cob,
    payload.coordination_of_benefits,
    eligibility.coordination_of_benefits,
    eligibility.cob,
    memberCoverage.coordination_of_benefits,
    memberCoverage.cob,
    ci.cob
  );
  if (!hasContent(cob)) {
    return { present: false, fields: [], raw: null };
  }
  const record = isPlainObject(cob) ? cob : { value: cob };
  const fields = [
    ["Primary", pickKey(record, ["primary", "is_primary", "payer_sequence"])],
    ["Secondary", pickKey(record, ["secondary", "is_secondary"])],
    ["Rule", pickKey(record, ["coordination_rule", "rule", "method"])],
    ["Other coverage", pickKey(record, ["other_coverage", "other_payer", "other_insurance"])],
    ["Notes", pickKey(record, ["notes", "summary", "text"])],
  ]
    .map(([label, value]) => {
      const display = formatField(value);
      return display ? { label, value: display } : null;
    })
    .filter(Boolean);
  return { present: fields.length > 0 || hasContent(record), fields, raw: record };
}

function pickOrthoBlock(payload, ci, payerKnowledge, benefitsSummary, dentalPolicy) {
  return firstRecord(
    payload.ortho,
    payload.orthodontics,
    payload.orthodontist,
    payload.ortho_policy,
    payload.orthodontics_policy,
    ci.ortho,
    ci.orthodontics,
    ci.ortho_policy,
    payerKnowledge.ortho,
    payerKnowledge.orthodontics,
    payerKnowledge.ortho_policy,
    benefitsSummary?.ortho,
    benefitsSummary?.orthodontics,
    dentalPolicy?.ortho,
    dentalPolicy?.orthodontics,
    ci.payer_policy?.ortho,
    ci.payer_policy?.orthodontics
  );
}

function normalizeOrthoSection(block, names, fallbackLabel) {
  const value = pickKey(block || {}, names);
  if (!hasContent(value)) {
    return { present: false, label: fallbackLabel, fields: [], notes: null, citations: [] };
  }
  if (!isPlainObject(value)) {
    return {
      present: true,
      label: fallbackLabel,
      fields: [{ label: fallbackLabel, value: displayOrMissing(value) }],
      notes: null,
      citations: [],
    };
  }
  const fields = [
    ["Covered", pickKey(value, ["covered", "is_covered", "status"])],
    ["Age", pickKey(value, ["age", "age_limit", "age_limits"])],
    ["Limit", pickKey(value, ["limit", "maximum", "max", "amount"])],
    ["Coinsurance", pickKey(value, ["coinsurance", "percent", "member_cost"])],
    ["Frequency", pickKey(value, ["frequency", "frequency_limit"])],
    ["Notes", pickKey(value, ["notes", "summary", "text", "description"])],
  ]
    .map(([label, field]) => {
      const display = formatField(field);
      return display ? { label, value: display } : null;
    })
    .filter(Boolean);
  return {
    present: fields.length > 0,
    label: fallbackLabel,
    fields,
    notes: formatField(pickKey(value, ["notes", "summary", "text"])),
    citations: collectCitations(value.citations, value.evidence),
    raw: value,
  };
}

function deriveOrthoFromStedi(eligibility, memberCoverage, ci) {
  const serviceRows = [
    ...asArray(eligibility.service_benefits),
    ...asArray(memberCoverage.service_benefits),
    ...asArray(ci.member_benefits?.service_benefits),
  ].filter((row) => (row.service_types || []).some(isOrthoLabel));

  const financialRows = asArray(eligibility.financial_benefits).filter((row) =>
    (row.service_types || []).some(isOrthoLabel)
  );

  const limitations = [
    ...asArray(ci.limitations),
    ...asArray(eligibility.limitations),
  ].filter((row) => (row.service_types || []).some(isOrthoLabel) || isOrthoLabel(row.text));

  const matrix = buildBenefitMatrix(serviceRows, financialRows);
  const orthoMatrix = matrix.find((row) => isOrthoLabel(row.label)) || matrix[0] || null;

  const deductibles = financialRows.filter(
    (row) => String(row.benefit_type || "").toLowerCase() === "deductible"
  );
  const deductibleDisplay = deductibles.length
    ? displayOrMissing(deductibles.find((row) => String(row.network || "").includes("IN"))?.amount || deductibles[0].amount)
    : null;

  const limitationRows = limitations
    .map((lim) => ({
      text:
        formatField(lim.text) ||
        formatField(lim.additional_information) ||
        asArray(lim.service_types).join(", "),
      quantity: formatField(lim.quantity),
      status: formatField(lim.status),
    }))
    .filter((row) => row.text || row.quantity);

  return {
    derived: true,
    has_category: Boolean(serviceRows.length || financialRows.length || limitationRows.length),
    plan_in: orthoMatrix?.in_network || "—",
    plan_out: orthoMatrix?.out_of_network || "—",
    member_in: orthoMatrix?.member_in || "—",
    member_out: orthoMatrix?.member_out || "—",
    deductible: deductibleDisplay,
    limitations: limitationRows,
  };
}

export function extractOrthoPolicy(payload) {
  const ci = payload.coverage_intelligence || {};
  const payerKnowledge = payload.payer_knowledge || {};
  const eligibility = payload.eligibility || {};
  const memberCoverage = payload.member_coverage || {};
  const benefitsSummary = firstRecord(payload.benefits_summary, ci.benefits_summary) || {};
  const dentalPolicy = firstRecord(
    payload.dental_policy,
    payload.lakehouse_policy,
    payload.payer_policy,
    ci.dental_policy,
    payerKnowledge.dental_policy
  ) || {};

  const block = pickOrthoBlock(payload, ci, payerKnowledge, benefitsSummary, dentalPolicy);
  const derived = deriveOrthoFromStedi(eligibility, memberCoverage, ci);
  const enrichmentPresent = hasContent(block);
  const source = enrichmentPresent
    ? "lakehouse"
    : derived.has_category
      ? "stedi"
      : "none";

  const record = isPlainObject(block) ? block : {};
  const ageLimits = asArray(
    pickKey(record, ["age_limits", "age_limit", "ageLimitations", "age"])
  )
    .map((item) => formatField(item))
    .filter(Boolean);

  const documentation = asArray(
    pickKey(record, ["documentation", "documentation_requirements", "required_documents"])
  )
    .map((item) => {
      if (!isPlainObject(item)) {
        const text = formatField(item);
        return text ? { text, citations: [] } : null;
      }
      const text =
        formatField(pickKey(item, ["text", "requirement", "summary", "description", "name"])) ||
        "Documentation requirement";
      return {
        text,
        notes: formatField(item.notes),
        citations: collectCitations(item.citations, item.evidence),
      };
    })
    .filter(Boolean);

  const citations = collectCitations(
    record.citations,
    record.evidence,
    documentation.flatMap((row) => row.citations)
  );

  const extraFields = [];
  if (isPlainObject(block)) {
    const known = new Set(
      [
        "covered",
        "lifetime_max",
        "lifetime_maximum",
        "remaining_lifetime_max",
        "age_limits",
        "age_limit",
        "phase_i",
        "phase_ii",
        "phase_1",
        "phase_2",
        "banding",
        "appliance",
        "retention",
        "documentation",
        "documentation_requirements",
        "citations",
        "evidence",
        "coinsurance",
        "deductible",
        "notes",
        "summary",
        "limitations",
      ].map(normalizeKey)
    );
    for (const [key, value] of Object.entries(record)) {
      if (known.has(normalizeKey(key))) continue;
      if (isPlainObject(value) || Array.isArray(value)) continue;
      const display = formatField(value);
      if (display) extraFields.push({ label: key.replace(/_/g, " "), value: display });
    }
  }

  return {
    present: enrichmentPresent || derived.has_category,
    source,
    covered: displayOrMissing(pickKey(record, ["covered", "is_covered", "status", "coverage_status"])),
    lifetime_max: displayOrMissing(
      pickKey(record, ["lifetime_max", "lifetime_maximum", "lifetimeMaximum", "max_lifetime"])
    ),
    remaining_lifetime_max: displayOrMissing(
      pickKey(record, ["remaining_lifetime_max", "lifetime_remaining", "remaining_maximum"])
    ),
    deductible: displayOrMissing(pickKey(record, ["deductible"])) !== "Not provided"
      ? displayOrMissing(pickKey(record, ["deductible"]))
      : derived.deductible || "Not provided",
    coinsurance: displayOrMissing(pickKey(record, ["coinsurance"])),
    plan_in: derived.plan_in,
    plan_out: derived.plan_out,
    member_in: derived.member_in,
    member_out: derived.member_out,
    age_limits: ageLimits,
    phase_i: normalizeOrthoSection(record, ["phase_i", "phaseI", "phase_1", "phase1"], "Phase I"),
    phase_ii: normalizeOrthoSection(record, ["phase_ii", "phaseII", "phase_2", "phase2"], "Phase II"),
    banding: normalizeOrthoSection(record, ["banding", "initial_banding", "banding_appliance"], "Banding"),
    appliance: normalizeOrthoSection(record, ["appliance", "appliances", "appliance_therapy"], "Appliance"),
    retention: normalizeOrthoSection(record, ["retention", "retention_period", "retainers"], "Retention"),
    documentation,
    limitations: derived.limitations,
    notes: formatField(pickKey(record, ["notes", "summary", "text"])),
    extra_fields: extraFields,
    citations,
    raw: block,
  };
}

export function extractDentalPolicy(payload) {
  const ci = payload.coverage_intelligence || {};
  const payerKnowledge = payload.payer_knowledge || {};
  const payerPolicy = ci.payer_policy || {};
  const dentalPolicy = firstRecord(
    payload.dental_policy,
    payload.lakehouse_policy,
    payload.payer_policy,
    ci.dental_policy,
    payerKnowledge.dental_policy
  );

  const lookup = firstRecord(
    dentalPolicy?.lookup,
    payerKnowledge.lookup,
    {
      payer: payerPolicy.payer,
      plan: payerPolicy.plan,
      state: payerPolicy.state,
      codes: payerPolicy.codes,
    }
  ) || {};

  const codeRules = asArray(
    firstRecord(
      dentalPolicy?.code_rules,
      payerKnowledge.code_rules,
      payerPolicy.code_rules
    )
  );
  const documentation = asArray(
    firstRecord(
      dentalPolicy?.documentation_requirements,
      payerKnowledge.documentation_requirements,
      payerPolicy.documentation_requirements
    )
  );
  const priorAuth = asArray(
    firstRecord(
      dentalPolicy?.prior_auth_policies,
      payerKnowledge.prior_auth_policies,
      payerPolicy.prior_auth_policies
    )
  );

  const status =
    formatField(
      firstRecord(
        payload.payer_knowledge_status,
        payerKnowledge.status,
        payerPolicy.knowledge_status,
        dentalPolicy?.status
      )
    ) || "";

  const present = Boolean(
    formatField(lookup.payer) ||
      formatField(lookup.plan) ||
      codeRules.length ||
      documentation.length ||
      priorAuth.length
  );

  return {
    present,
    status,
    lookup: {
      payer: formatField(lookup.payer),
      plan: formatField(lookup.plan),
      state: formatField(lookup.state),
      product: formatField(lookup.product),
      lob: formatField(lookup.lob),
      coverage_id: formatField(lookup.coverage_id),
      service: formatField(lookup.service),
      codes: asArray(lookup.codes),
    },
    code_rules: codeRules,
    documentation_requirements: documentation.map((item) =>
      isPlainObject(item)
        ? {
            text:
              formatField(pickKey(item, ["text", "requirement", "summary", "description"])) ||
              "Documentation requirement",
            applies_to: formatField(item.applies_to),
            citations: collectCitations(item.citations),
          }
        : { text: formatField(item), applies_to: null, citations: [] }
    ),
    prior_auth_policies: priorAuth,
    unmatched_codes: asArray(payerKnowledge.unmatched_codes),
    source_evidence: payerKnowledge.source_evidence || dentalPolicy?.source_evidence || null,
  };
}

function extractPayerIds(payload, eligibility, intake) {
  const insurance = asArray(intake.insurance);
  const primary = insurance.find((row) => row.is_primary) || insurance[0] || {};
  const ids = [
    { label: "Payer ID", value: formatField(intake.payer?.payer_id || eligibility.payer?.payer_id || primary.payer_id) },
    { label: "Payer name", value: formatField(intake.payer?.payer_name || eligibility.payer?.payer_name) },
    { label: "Coverage ID", value: formatField(primary.coverage_id) },
    { label: "Group", value: formatField(eligibility.member?.group_number || eligibility.plan?.group_number || primary.group_number) },
    { label: "Member ID", value: formatField(eligibility.member?.member_id || primary.member_id) },
  ].filter((row) => row.value);
  return ids;
}

function extractFilingHints(eligibility, ci) {
  const hints = [];
  for (const msg of asArray(eligibility.messages)) {
    const text = formatField(msg);
    if (text) hints.push({ kind: "payer_message", text });
  }
  for (const item of asArray(ci.action_items)) {
    const code = String(item.code || "").toUpperCase();
    const message = formatField(item.message);
    if (!message) continue;
    if (
      /FILING|PAYMENT|PREDETERMINATION|PRIOR_AUTH|BILL|TIMELY|COB|MAXIMUM|NOT_A_PAYMENT/i.test(
        `${code} ${message}`
      )
    ) {
      hints.push({ kind: item.severity || "info", code: item.code, text: message });
    }
  }
  return hints;
}

export function extractClaimsHints(payload) {
  const ci = payload.coverage_intelligence || {};
  const eligibility = payload.eligibility || {};
  const memberCoverage = payload.member_coverage || {};
  const intake = payload.intake || {};
  const cob = extractCob(payload, eligibility, memberCoverage, ci);
  const dental = extractDentalPolicy(payload);
  const priorAuth = ci.prior_authorization || null;
  const billingLimitations = [
    ...asArray(ci.limitations),
    ...asArray(eligibility.limitations),
  ]
    .filter((row) => !isWaitingItem(row))
    .map((row) => ({
      text:
        formatField(row.text) ||
        formatField(row.additional_information) ||
        asArray(row.service_types).join(", ") ||
        "Limitation",
      quantity: formatField(row.quantity),
      code: formatField(row.code),
      source: formatField(row.source),
    }))
    .filter((row) => row.text);

  return {
    payer_ids: extractPayerIds(payload, eligibility, intake),
    network: formatField(ci.network_status || eligibility.network_status || memberCoverage.network?.value),
    cob,
    filing_hints: extractFilingHints(eligibility, ci),
    prior_authorization: priorAuth,
    prior_auth_policies: dental.prior_auth_policies,
    billing_limitations: billingLimitations,
    source_evidence: eligibility.source_evidence || memberCoverage.source_evidence || null,
  };
}

export function mapPatientWorkspace(bffResponse) {
  const payload = unwrapPayload(bffResponse);
  const pms = payload.pms_patient || {};
  const intake = payload.intake || {};
  const patient = intake.patient || {};
  const appointment = intake.appointment || {};
  const subscriber = intake.subscriber || {};
  const insurance = (intake.insurance && intake.insurance[0]) || {};
  const eligibility = payload.eligibility || {};
  const memberCoverage = payload.member_coverage || {};
  const ci = payload.coverage_intelligence || {};
  const payerKnowledge = payload.payer_knowledge || {};
  const financial = ci.financial_summary || {};
  const memberBenefits = ci.member_benefits || memberCoverage || {};
  const benefitsSummary = firstRecord(payload.benefits_summary, ci.benefits_summary) || {};

  const firstName = pms.firstName || patient.first_name || eligibility.member?.first_name || "";
  const lastName = pms.lastName || patient.last_name || eligibility.member?.last_name || "";
  const memberId =
    pms.primarySubscriberId ||
    insurance.member_id ||
    eligibility.member?.member_id ||
    subscriber.subscriber_id ||
    "";

  const limitations = (ci.limitations || eligibility.limitations || []).filter(
    (row) => row && (row.text || row.additional_information?.length || row.quantity?.value)
  );

  const messages = eligibility.messages || [];
  const codeRules = payerKnowledge.code_rules || ci.payer_policy?.code_rules || [];
  const waitingPeriods = extractWaitingPeriods(payload, ci, eligibility);
  const frequencyLimits = extractFrequencyLimits(payload, ci, payerKnowledge, codeRules);
  const cob = extractCob(payload, eligibility, memberCoverage, ci);
  const ortho = extractOrthoPolicy(payload);
  const dentalPolicy = extractDentalPolicy(payload);
  const claims = extractClaimsHints(payload);
  const citations = collectCitations(
    ci.citations,
    payload.citations,
    ortho.citations,
    dentalPolicy.documentation_requirements.flatMap((row) => row.citations)
  );

  const plannedServices = appointment.planned_services || intake.requested_services || [];
  const procedureRules = codeRules.filter((rule) =>
    plannedServices.some((code) => String(rule.code || "").toUpperCase() === String(code).toUpperCase())
  );

  return {
    patient_id: String(payload.patient_id || patient.patient_id || ""),
    first_name: firstName,
    last_name: lastName,
    member_id: memberId,
    date_of_birth: patient.date_of_birth || pms.birthDate || eligibility.member?.date_of_birth || "",
    gender: patient.gender || "",
    payer_name:
      intake.payer?.payer_name ||
      eligibility.payer?.payer_name ||
      pms.primaryInsuranceName ||
      insurance.insurance_name ||
      "",
    payer_id: intake.payer?.payer_id || eligibility.payer?.payer_id || insurance.payer_id || "",
    plan_name:
      formatField(benefitsSummary.plan) ||
      eligibility.plan?.group_description ||
      eligibility.plan?.plan_description ||
      payerKnowledge.lookup?.plan ||
      "",
    group_number:
      formatField(benefitsSummary.group_number) ||
      eligibility.member?.group_number ||
      insurance.group_number ||
      "",
    coverage_status:
      ci.coverage_status ||
      memberCoverage.coverage_status ||
      eligibility.coverage_status ||
      payload.eligibility_status ||
      "UNKNOWN",
    eligibility_status: payload.eligibility_status || eligibility.eligibility_status || "",
    network_status: ci.network_status || eligibility.network_status || memberBenefits.network?.value || "",
    coverage_period: memberBenefits.coverage_period || eligibility.coverage_period || benefitsSummary.coverage_period || null,
    visit_readiness: mapVisitReadiness(payload.assessment?.readiness_status),
    readiness_status: payload.assessment?.readiness_status || "",
    readiness_score: payload.assessment?.readiness_score,
    recommended_action: payload.assessment?.recommended_action || "",
    appointment_id: appointment.appointment_id || null,
    appointment_datetime: appointment.appointment_datetime || null,
    appointment_status: appointment.appointment_status || null,
    reason_for_visit: appointment.reason_for_visit || "",
    planned_services: plannedServices,
    review_id: payload.review?.review_id || payload.review_id || null,
    review: payload.review || null,
    provider_name: intake.provider?.name || "",
    provider_npi: intake.provider?.npi || "",
    deductible: formatMoneyField(financial.member_deductible || memberBenefits.deductible),
    remaining_deductible: formatMoneyField(
      financial.member_remaining_deductible || memberBenefits.remaining_deductible
    ),
    annual_maximum: formatMoneyField(financial.member_annual_maximum || memberBenefits.annual_maximum),
    remaining_maximum: formatMoneyField(
      financial.member_remaining_maximum || memberBenefits.remaining_maximum
    ),
    copay: formatMoneyField(financial.member_copay || memberBenefits.copay),
    coinsurance: formatMoneyField(financial.member_coinsurance || memberBenefits.coinsurance),
    plan_member_cost: formatMoneyField(financial.plan_member_cost),
    financial_notes: financial.notes || [],
    benefit_matrix: buildBenefitMatrix(
      firstArray(
        memberBenefits.service_benefits,
        eligibility.service_benefits,
        memberCoverage.service_benefits
      ),
      asArray(eligibility.financial_benefits)
    ),
    limitations,
    waiting_periods: waitingPeriods,
    frequency_limits: frequencyLimits,
    cob,
    messages,
    exclusions: asArray(eligibility.exclusions),
    action_items: ci.action_items || [],
    service_assessments: ci.service_assessments || [],
    prior_authorization: ci.prior_authorization || null,
    payer_policy: ci.payer_policy || payerKnowledge,
    code_rules: codeRules,
    procedure_rules: procedureRules,
    dental_policy: dentalPolicy,
    ortho,
    claims,
    citations,
    evidence: ci.evidence || payload.evidence || [],
    coverage_intelligence_status: payload.coverage_intelligence_status || ci.status || "",
    payer_knowledge_status: payload.payer_knowledge_status || "",
    grounding_status: payload.grounding_status || ci.grounding_status || "",
    publish_status: payload.publish_status || ci.publish_status || "",
    not_a_payment_guarantee: ci.not_a_payment_guarantee !== false,
    source: bffResponse?.source || "upstream",
    cached_at: bffResponse?.cached_at || null,
    eligibility_source: eligibility.source_evidence || memberCoverage.source_evidence || null,
    raw: payload,
  };
}

export function answerCoverageQuestion(workspace, question) {
  const q = String(question || "").toLowerCase();
  if (!workspace) return "Open a patient to ask about their coverage.";
  if (q.includes("ortho") || q.includes("braces") || q.includes("banding") || q.includes("retention")) {
    const ortho = workspace.ortho;
    if (!ortho?.present) {
      return "No orthodontist policy details are on this Coverage payload. The Orthodontics panel will stay empty until Stedi or lakehouse policy includes them.";
    }
    const bits = [
      `Plan coverage IN ${ortho.plan_in} / OON ${ortho.plan_out}`,
      `Lifetime max ${ortho.lifetime_max}`,
      ortho.age_limits.length ? `Age limits: ${ortho.age_limits.join("; ")}` : null,
    ].filter(Boolean);
    return `Orthodontics (${ortho.source}): ${bits.join(". ")}.`;
  }
  if (q.includes("status") || q.includes("active")) {
    return `${workspace.first_name}'s coverage status is ${workspace.coverage_status || "unknown"} (eligibility: ${workspace.eligibility_status || "n/a"}).`;
  }
  if (q.includes("deductible")) {
    return `Deductible is ${workspace.deductible}. Remaining deductible: ${workspace.remaining_deductible}.`;
  }
  if (q.includes("maximum") || q.includes("annual")) {
    return `Annual maximum: ${workspace.annual_maximum}. Remaining maximum: ${workspace.remaining_maximum}.`;
  }
  if (q.includes("waiting")) {
    const lines = (workspace.waiting_periods || []).map((row) => row.text).filter(Boolean);
    return lines.length ? lines.join(" ") : "No waiting-period details were provided on this payload.";
  }
  if (q.includes("cob") || q.includes("coordination")) {
    if (!workspace.cob?.present) return "No coordination-of-benefits details were provided.";
    return workspace.cob.fields.map((f) => `${f.label}: ${f.value}`).join(". ");
  }
  if (q.includes("prior auth") || q.includes("authorization") || q.includes("pa ")) {
    const pa = workspace.prior_authorization;
    if (!pa) return "No prior-authorization policy is attached to this coverage record.";
    return pa.summary || `Prior authorization required: ${pa.required ? "yes" : "no"}.`;
  }
  if (q.includes("limit")) {
    const lines = (workspace.limitations || [])
      .map((l) => l.text || (l.quantity && l.quantity.value) || "")
      .filter(Boolean)
      .slice(0, 4);
    return lines.length ? lines.join(" ") : "No explicit limitations text was provided.";
  }
  const firstAction = workspace.action_items?.[0];
  if (firstAction) {
    return `${workspace.first_name} ${workspace.last_name} — ${workspace.coverage_status}. Next: ${firstAction.message}`;
  }
  return `${workspace.first_name} ${workspace.last_name} has ${workspace.coverage_status || "unknown"} coverage with ${workspace.payer_name || "the mapped payer"}.`;
}
